import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HelpdeskNotificationsAPI } from "../../../../api/helpdeskNotifications";
import SearchableMultiUserSelect from "../../../../components/SearchableMultiUserSelect";

const EMPTY_NOTIFICATION = {
  id: "",
  name: "",
  channel: "email",
  enabled: true,
  recipients: [{ type: "ticket_requester" }],
  subject_template: "",
  body_template: "",
  elements: [
  { type: "latest_message" },
  { type: "ticket_button" }
],
};

export default function NotificationEditor({ mode, notificationId }) {
  const [notification, setNotification] = useState(EMPTY_NOTIFICATION);
  const [loading, setLoading] = useState(mode === "edit");
  const navigate = useNavigate();
  const [variables, setVariables] = useState([]);
  const textareaRef = useRef(null);
  const [activeField, setActiveField] = useState("body");
  const subjectRef = useRef(null);
  const [recipientTypes, setRecipientTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (mode === "edit") load();
    loadVariables();
    loadRecipientTypes();
    loadRoles();
    loadUsers();
  }, []);

  async function load() {
    const data = await HelpdeskNotificationsAPI.get(notificationId);
    setNotification({
      ...data,
      elements: Array.isArray(data.elements)
        ? data.elements
        : []
    });;
    setLoading(false);
  }

  async function loadVariables() {
    const res = await HelpdeskNotificationsAPI.listVariables();
    setVariables(res?.variables || []);
  }

  async function loadRoles() {
    const res = await HelpdeskNotificationsAPI.listRoleOptions();
    setRoles(res?.options || []);
  }

  async function loadUsers() {
      const res = await HelpdeskNotificationsAPI.listUsers();
      setUsers(res?.users || []);
    }

  async function loadRecipientTypes() {
      const res = await HelpdeskNotificationsAPI.listRecipientTypes();
      setRecipientTypes(res?.recipientTypes || []);
    }

    function insertVariable(variableKey) {

      const variable = variableKey === "message.body_html"
        ? `{{{${variableKey}}}}`
        : `{{${variableKey}}}`;

      // SUBJECT FIELD
      if (activeField === "subject") {

        const input = subjectRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;

        const before = notification.subject_template.substring(0, start);
        const after = notification.subject_template.substring(end);

        const newValue = before + variable + after;

        setNotification({
          ...notification,
          subject_template: newValue
        });

        setTimeout(() => {
          input.focus();
          input.selectionStart = input.selectionEnd =
            start + variable.length;
        }, 0);

        return;
      }

      // BODY FIELD
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const before = notification.body_template.substring(0, start);
      const after = notification.body_template.substring(end);

      const newValue = before + variable + after;

      setNotification({
        ...notification,
        body_template: newValue
      });

      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd =
          start + variable.length;
      }, 0);
    }

  async function previewTemplate() {
    try {
      const res = await HelpdeskNotificationsAPI.preview({
        subject: notification.subject_template,
        body: notification.body_template,
        elements: notification.elements,
      });

      const win = window.open("", "_blank");
      win.document.write(res.html);
      win.document.close();
    } catch (err) {
      console.error("Preview failed", err);
      alert("Preview failed");
    }
  }

  async function save() {
    if (mode === "create") {
      await HelpdeskNotificationsAPI.create(notification);
    } else {
      await HelpdeskNotificationsAPI.update(notificationId, notification);
    }

    navigate("/helpdesk/automation/notifications");
  }

  if (loading) return <div>Loading…</div>;

  return (
    <div className="p-6 space-y-6 max-w-3xl">

      <h1 className="text-xl font-semibold">
        {mode === "create" ? "New Notification" : "Edit Notification"}
      </h1>

      <div className="space-y-2">
        <label>ID</label>
        <input
          className="border p-2 w-full"
          value={notification.id}
          onChange={(e) =>
            setNotification({ ...notification, id: e.target.value })
          }
        />
      </div>

      <div className="space-y-2">
        <label>Name</label>
        <input
          className="border p-2 w-full"
          value={notification.name}
          onChange={(e) =>
            setNotification({ ...notification, name: e.target.value })
          }
        />
      </div>

      {/* Recipient selector */}
      <div className="space-y-2">
        <label>Recipient</label>
        <select
          className="border p-2"
          value={notification.recipients?.[0]?.type}
          onChange={(e) =>
            setNotification({
              ...notification,
              recipients: [{ type: e.target.value }],
            })
          }
        >
          {recipientTypes.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>


{notification.recipients?.[0]?.type === "roles" && (
  <div className="space-y-2">
    <label>Roles</label>

    <div className="border rounded p-2 space-y-2">
      {/* Selected roles */}
      {notification.recipients?.[0]?.roleNames?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {notification.recipients[0].roleNames.map((role) => (
            <button
              key={role}
              type="button"
              className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
              onClick={() => {
                const next = notification.recipients[0].roleNames.filter(
                  (r) => r !== role
                );

                setNotification({
                  ...notification,
                  recipients: [
                    {
                      type: "roles",
                      roleNames: next,
                    },
                  ],
                });
              }}
            >
              {role} ×
            </button>
          ))}
        </div>
      )}

      {/* Available roles */}
      <div className="max-h-40 overflow-y-auto border rounded">
        {roles.map((r) => {
          const selected =
            notification.recipients?.[0]?.roleNames?.includes(r.value);

          if (selected) return null;

          return (
            <button
              key={r.value}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-gray-100"
              onClick={() => {
                const next = [
                  ...(notification.recipients?.[0]?.roleNames || []),
                  r.value,
                ];

                setNotification({
                  ...notification,
                  recipients: [
                    {
                      type: "roles",
                      roleNames: next,
                    },
                  ],
                });
              }}
            >
              {r.label}
            </button>
          );
        })}
      </div>
    </div>
  </div>
)}

{notification.recipients?.[0]?.type === "explicit_users" && (
  <div className="space-y-2">
    <label>Users</label>

    <SearchableMultiUserSelect
      users={users}
      value={notification.recipients?.[0]?.userIds || []}
      onChange={(userIds) => {
        setNotification({
          ...notification,
          recipients: [
            {
              type: "explicit_users",
              userIds,
            },
          ],
        });
      }}
    />
  </div>
)}

      {notification.recipients?.[0]?.type === "email" && (
        <div className="space-y-2">
          <label>Email addresses (comma separated)</label>
          <textarea
            className="border p-2 w-full"
            value={(notification.recipients?.[0]?.emails || []).join(", ")}
            onChange={(e) => {
              const emails = e.target.value
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean);

              setNotification({
                ...notification,
                recipients: [
                  {
                    type: "email",
                    emails,
                  },
                ],
              });
            }}
          />
        </div>
      )}

      <div className="space-y-2">
        <label>Subject template</label>
          <input
            ref={subjectRef}
            className="border p-2 w-full"
            value={notification.subject_template}
            onFocus={() => setActiveField("subject")}
            onChange={(e) =>
              setNotification({
                ...notification,
                subject_template: e.target.value,
              })
            }
          />
      </div>

      <div className="space-y-2">
        <label>Email body</label>
        <textarea
          ref={textareaRef}
          onFocus={() => setActiveField("body")}
          rows="8"
          className="border p-2 w-full"
          value={notification.body_template}
          onChange={(e) =>
            setNotification({
              ...notification,
              body_template: e.target.value,
            })
          }
        />
      </div>

      <div className="space-y-2">
        <label>Email elements</label>

        <div className="space-y-2">
          {(notification.elements || []).map((el, index) => (
            <div key={index} className="flex items-center gap-2 border p-2 rounded">

              <span className="text-sm flex-1">{el.type}</span>

              <button
                type="button"
                onClick={() => {
                  const copy = [...notification.elements];
                  if (index > 0) {
                    [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
                    setNotification({ ...notification, elements: copy });
                  }
                }}
              >
                ↑
              </button>

              <button
                type="button"
                onClick={() => {
                  const copy = [...notification.elements];
                  if (index < copy.length - 1) {
                    [copy[index + 1], copy[index]] = [copy[index], copy[index + 1]];
                    setNotification({ ...notification, elements: copy });
                  }
                }}
              >
                ↓
              </button>

              <button
                type="button"
                onClick={() => {
                  const copy = notification.elements.filter((_, i) => i !== index);
                  setNotification({ ...notification, elements: copy });
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              setNotification({
                ...notification,
                elements: [...(notification.elements || []), { type: "ticket_button" }]
              })
            }
            className="px-2 py-1 border rounded"
          >
            + Button
          </button>

          <button
            type="button"
            onClick={() =>
              setNotification({
                ...notification,
                elements: [...(notification.elements || []), { type: "latest_message" }]
              })
            }
            className="px-2 py-1 border rounded"
          >
            + Latest message
          </button>

          <button
            type="button"
            onClick={() =>
              setNotification({
                ...notification,
                elements: [...(notification.elements || []), { type: "conversation_history" }]
              })
            }
            className="px-2 py-1 border rounded"
          >
            + History
          </button>
        </div>
      </div>

      {variables.length > 0 && (
        <div className="mt-3">
          <div className="text-sm font-medium mb-2">
            Insert variable
          </div>

          <div className="flex flex-wrap gap-2">
            {variables.map((v) => (
              <button
                key={v.key}
                type="button"
                title={v.key === "message.body_html" ? `Inserts {{{${v.key}}}} so formatted HTML is not escaped` : `Inserts {{${v.key}}}`}
                onClick={() => insertVariable(v.key)}
                className="px-2 py-1 text-xs border rounded bg-gray-50 hover:bg-gray-100"
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={previewTemplate}
        className="px-3 py-2 border rounded">
        Preview
      </button>

      <div className="flex gap-2 pt-4">
        <button
          onClick={save}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Save
        </button>

        <button
          onClick={() =>
            navigate("/helpdesk/automation/notifications")
          }
          className="px-4 py-2 border rounded"
        >
          Cancel
        </button>
      </div>

    </div>
  );
}